/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  I2cButton,
  I2cIcon,
  I2cAvatar,
  I2cBadge,
  I2cPagination,
  I2cDialog,
  I2cFormatDate,
  I2cDropdown,
  I2cMenu,
  I2cMenuItem,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../../Utils/shared-styles/Authentication.scss";
import "./Users.scss";
import "./UserProjects.scss";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import ProjectManagmentService from "../../Services/ProjectManagementService";
import { OverlayLoader } from "../../Components/Loader/Loader";
import UserManagmentService from "../../Services/UsermanagmentService";
import { getBadgeVariant } from "../../Utils/stringUtls";
import AssignProjects from "./AssignProjects";
import ConfirmaionModal from "../../Components/ConfirmationModal/ConfirmationModal";
import SalesService from "../../Services/SalesService";
import ToastContainer from "../../Components/SharedComponent/ToastContainer/ToastContainer";
export const extractKeyValues = (dataArray, keysArray) => {
  let result = [];

  for (let item of dataArray) {
    for (let nestedKey in item) {
      if (item.hasOwnProperty(nestedKey)) {
        let nestedObject = item[nestedKey];

        for (let key of keysArray) {
          if (nestedObject.hasOwnProperty(key)) {
            let extractedData = {};
            extractedData[key] = nestedObject[key];
            result.push(extractedData);
          }
        }
      }
    }
  }

  return result;
};
/**
 * UserProjects Component displays a list of projects assigned to user.
 *
 * @component
 */
const UserProjects = () => {
  // Hooks for navigation and query parameters
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State variables
  const { id: userId } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [projects, setProjects] = useState([]);
  const [pagination, setPagination] = useState([]);
  const [user, setUser] = useState([]);
  const [avatarImage, setAvatarImage] = useState(null);
  const [open, setOpen] = useState(false);
  const [showConfirmatonModal, setShowConfirmatonModal] = useState(false);
  const [deleteProjectObj, setDeleteProjectObj] = useState(null);
  const [isAssignProjectSuccess, setIsAssignProjectSuccess] = useState(false);
  const queryClient = useQueryClient();
  
  // Toast references for notifications
  const successAlertRef = useRef(null);
  const errorAlertRef = useRef(null);
  const [toastMessage, setToastMessage] = useState({ title: '', description: '' });
  
  // Get query parameters for alerts
  const isProjectAdded = searchParams.get("project-added") ?? false;
  const isProjectUpdated = searchParams.get("project-updated") ?? false;

  // Fetching forms by user id query
  const queryKey = ["projects", userId, isAssignProjectSuccess, currentPage];
  const {
    data: projectsData,
    isFetching,
    isLoading,
  } = useQuery(
      queryKey,
      () => ProjectManagmentService.getProjects(userId, currentPage),
      { refetchOnWindowFocus: false },
  );

  // Fetching user data by user id query
  const { data: userData } = useQuery(
      ["userData", userId],
      () => UserManagmentService.getSingleUser(userId),
      { refetchOnWindowFocus: false },
  );

  // Function to show toast notifications
  const pushToast = useCallback((alert, title, description) => {
    setToastMessage({ title, description });
    setTimeout(() => {
      if (alert.current) {
        alert.current.style.transform = 'translate3d(0, 100%, 150px) scale(1)';
        alert.current.toast();
      }
    }, 100);
  }, []);
  
  /**
   * Handle button click to navigate to the current location without query parameters.
   */
  const handleButtonClick = useCallback(() => {
    navigate(".", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (projectsData) {
      setProjects(projectsData?.data?.forms);
      setPagination({
        total: projectsData.data.total,
        per_page: projectsData.data.per_page,
      });
    }
  }, [projectsData, isAssignProjectSuccess]);

  useEffect(() => {
    if (userData) {
      setUser(userData?.data?.user);
      setAvatarImage(`data:image/jpeg;base64,${userData?.data?.user.avatar}`);
    }
  }, [userData]);
  
  // Show toast notifications based on query parameters
  useEffect(() => {
    if (isProjectAdded) {
      pushToast(
        successAlertRef,
        'Success',
        'Project has been added successfully'
      );
      setTimeout(() => handleButtonClick(), 100);
    }
    if (isProjectUpdated) {
      pushToast(
        successAlertRef,
        'Success',
        'Project updated successfully'
      );
      setTimeout(() => handleButtonClick(), 100);
    }
  }, [isProjectAdded, isProjectUpdated, handleButtonClick, pushToast]);
  
  const handleAssignSuccess = useCallback((result) => {
    setIsAssignProjectSuccess((prev) => !prev);
    if (result.success) {
      pushToast(
        successAlertRef,
        'Success',
        'Project(s) assigned successfully'
      );
    } else {
      pushToast(
        errorAlertRef,
        'Error',
        'Failed to assign project(s). Please try again.'
      );
    }
  }, [pushToast]);
  
  function getValueByKey(array, keyToFind) {
    for (let obj of array) {
      if (obj.hasOwnProperty(keyToFind)) {
        return obj[keyToFind];
      }
    }
    return null; // Return null if the key is not found
  }
  const saleDeleteMutation = useMutation(
      (projectID) => SalesService.deleteForm(projectID),
      {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKey);
          setDeleteProjectObj(false);
        },
        onError: (e) => {
          setDeleteProjectObj(false);
        },
      },
  );
  const handleProjectDelete = () => {
    saleDeleteMutation.mutate(deleteProjectObj.hashedFormID);
  };
  return (
      <React.Fragment>
        <ConfirmaionModal
            open={deleteProjectObj ? true : false}
            setOpen={() => setDeleteProjectObj(null)}
            title={"Delete Record"}
            headerArt={"cs-linear-alert-circle"}
            message={"Are you sure you want to delete the Sale?"}
            variant="danger"
            layout={"layoutCenter"}
            cancelButtonText={"Cancel"}
            confirmButtonText={"Delete"}
            confirmBtnVariant={"primary"}
            handleConfirmation={handleProjectDelete}
            disableConfirmBtn={saleDeleteMutation.isLoading}
            disableCancelBtn={saleDeleteMutation.isLoading}
        />
        {/* User Forms Listing Layout */}
        <div className="layout_aside user-form-container users-listing">
          <div onClick={() => navigate("/users")} className="go-back-button">
            <I2cIcon
                name="cs-linear-chevron-left"
                label="icon"
                library="default"
                className="go-back-button-icon"
            />
            Back
          </div>

          <div i2c-grid="grid" className="user-info-block">
            <div i2c-grid="4@md">
              <I2cAvatar
                  info={user.designation}
                  label={user.name}
                  alt={user.name}
                  size="2xl"
                  image={`data:image/jpeg;base64,${user.avatar}` || avatarImage}
              >
                <div slot="name">
                  <span className="userName">{user.name}</span>{" "}
                  <I2cBadge
                      className="desigBadge"
                      variant="light-primary"
                      size="small"
                  >
                    {user.role_nice_name}
                  </I2cBadge>
                </div>
              </I2cAvatar>
            </div>
            <div i2c-grid="8@md" className="v-align">
              <div className="btn-holder">
                <I2cButton variant="secondary gray" onClick={() => setOpen(true)}>
                  Assign New Project
                </I2cButton>
                <I2cButton
                    variant="primary"
                    onClick={() => {
                      navigate(`/users/${user.id}`);
                    }}
                >
                  Edit Profile
                </I2cButton>
              </div>
            </div>
            <I2cDialog
                className="unclosable overlay custom-dialog"
                label="Assign Project"
                headerArt=""
                headerArtStyle=""
                style={{
                  "--width":
                      "calc(var(--i2c-spacing-64) * 2 + var(--i2c-spacing-32) + var(--i2c-spacing-16))",
                }}
                variant="primary"
                uncloseableOverlay={true}
                open={open}
                onI2cHide={(event) => {
                  if (event.target.tagName === "I2C-DIALOG") setOpen(false);
                }}
                closable
            >
              <AssignProjects
                  closeModal={() => setOpen(false)}
                  onAssignSuccess={handleAssignSuccess}
              />
            </I2cDialog>
          </div>
          <div className="user-form-header">
            <h2>Client Request(s)</h2>
          </div>
          {/* Projects Listing Section */}
          {projects?.length ? (
              <div className="users-listing-section">
                {projects.map((project) => {
                  const proj = JSON.parse(project.form_data);
                  const keysToExtract = [
                    "pd_clinet_name",
                    "pi_program_type",
                    "pd_region",
                    "pd_team_avail",
                    "pd_go_live",
                    "ci_businessName",
                    "ci_companyPhone",
                    "cp_fi_fi_name",
                    "cp_fi_phone_num",
                  ];
                  const extractedData = extractKeyValues(proj, keysToExtract);
                  const variant = getBadgeVariant(project.form_status_name);
                  const goLiveDate = getValueByKey(extractedData, "pd_go_live");
                  return (
                      <div
                          key={project.id}
                          className="users-listing-section-single-user"
                      >
                        <div className="single-user-parent">
                          <div className="single-user-inner1">
                            {/* Project Details */}
                            <div className="single-user-inner1-child1">
                              <div className="single-user-detail">
                                {/* Project Name and status */}
                                <div className="single-user-name">
                                  <strong className="project-name">
                                    {/* {getValueByKey(extractedData, "pd_clinet_name")} */}
                                    {project.name}
                                  </strong>
                                  <I2cBadge variant={`light-${variant}`} size="small">
                                    {project.form_status_name}
                                  </I2cBadge>
                                </div>
                                {/* User Name & Creation Date */}
                                <div className="project-user-name-date">
                                  <span>By {project.added_by_name}</span>
                                  <div className="date-holder">
                                    <I2cFormatDate
                                        month="short"
                                        day="numeric"
                                        year="numeric"
                                        date={project.created_at}
                                    />{" "}
                                    -
                                    <I2cFormatDate
                                        date={project.created_at}
                                        hour="numeric"
                                        minute="numeric"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* User Projects and Actions */}
                            <div className="single-user-inner1-child2">
                              {/* Edit User Button */}
                              <I2cButton
                                  variant="secondary gray"
                                  onlyicon
                                  size="x-small"
                                  onClick={() => {
                                    if (project.form_type === 1)
                                      navigate(`/debit/edit/${project?.hashedFormID}`);
                                    else if (project.form_type === 2)
                                      navigate(`/credit/edit/${project?.hashedFormID}`);
                                  }}
                              >
                                <I2cIcon name="linear-edit-2" />
                              </I2cButton>
                              {/* Edit User Button */}

                              <I2cDropdown
                                  distance="10"
                                  placement="bottom-end"
                                  width="calc(var(--i2c-spacing-40) + var(--i2c-spacing-0.5))"
                              >
                                <I2cButton
                                    slot="trigger"
                                    variant="secondary gray"
                                    size="x-small"
                                >
                                  <I2cIcon name="cs-linear-dots-vertical" />
                                </I2cButton>
                                <I2cMenu className="add-new-project-menu">
                                  <I2cMenuItem
                                      size="small"
                                      onClick={() => {
                                        if (project.form_type === 1)
                                          navigate(
                                              `/debit/edit/${project?.hashedFormID}?review-screen=true`,
                                          );
                                        else if (project.form_type === 2)
                                          navigate(
                                              `/credit/edit/${project?.hashedFormID}?review-screen=true`,
                                          );
                                      }}
                                      className="add-project-menu-item"
                                  >
                                    <span>Review</span>
                                    <I2cIcon
                                        slot="prefix"
                                        name="cs-linear-layers-two"
                                    />
                                  </I2cMenuItem>
                                  {/* <I2cMenuItem
                              size="small"
                              // onClick={() => navigate('/debit/add')}
                              className="add-project-menu-item"
                            >
                              <span>Download</span>
                              <I2cIcon
                                slot="prefix"
                                name="linear-document-download"
                              />
                            </I2cMenuItem> */}
                                  <I2cMenuItem
                                      size="small"
                                      className="add-project-menu-item red"
                                      onClick={() => {
                                        setDeleteProjectObj(project);
                                        setShowConfirmatonModal(true);
                                      }}
                                  >
                                    <span>Delete</span>
                                    <I2cIcon slot="prefix" name="linear-trash" />
                                  </I2cMenuItem>
                                </I2cMenu>
                              </I2cDropdown>
                            </div>
                          </div>
                          {/* Project Details */}
                          <div className="single-user-inner2">
                            {/* Program Type */}
                            <div className="single-user-inner2-child">
                              <div className="child-header">Program Type</div>
                              <div className="child-value">
                                {project.form_type === 1 ? "Prepaid/Debit" : "Credit"}
                              </div>
                            </div>

                            {/* Region */}
                            <div className="single-user-inner2-child">
                              <div className="child-header">Region</div>
                              {/*<span className="fflag fflag-AL" style={{backgroundImage: "url(/public/static/dist/assets/images/flagSprite42.png)"}}></span>*/}
                              <div className="child-value">
                                {getValueByKey(extractedData, "pd_region") || '-'}
                              </div>
                            </div>

                            {/* Target Dates */}
                            <div className="single-user-inner2-child">
                              <div className="child-header">Target Go Live Date</div>
                              <div className="child-value">
                                {goLiveDate ? (
                                    <I2cFormatDate
                                        month="short"
                                        day="numeric"
                                        year="numeric"
                                        date={goLiveDate}
                                    />
                                ) : (
                                    "-"
                                )}
                              </div>
                            </div>

                            {/* Bank */}
                            <div className="single-user-inner2-child">
                              <div className="child-header">Bank</div>
                              <div className="child-value">
                                {getValueByKey(extractedData, "ci_businessName") || '-'}
                              </div>
                            </div>

                            {/* Phone # */}
                            <div className="single-user-inner2-child">
                              <div className="child-header">Phone Number</div>
                              <div className="child-value">
                                {getValueByKey(extractedData, "ci_companyPhone") || '-'}
                              </div>
                            </div>
                          </div>
                          <div className="single-user-inner1-child2-mobile">
                            <I2cButton variant="secondary" size="medium">
                              <I2cIcon name="linear-edit-2" />
                              <span className="icons-text">Edit</span>
                            </I2cButton>

                            <I2cDropdown
                                distance="10"
                                placement="bottom-end"
                                width="100%"
                                className="more-btn-mob"
                            >
                              <I2cButton
                                  slot="trigger"
                                  variant="secondary gray"
                                  size="medium"
                              >
                                <I2cIcon name="linear-more" />
                                <span className="icons-text">More</span>
                              </I2cButton>
                              <I2cMenu className="add-new-project-menu">
                                <I2cMenuItem
                                    size="small"
                                    onClick={() => navigate("/debit/add")}
                                    className="add-project-menu-item"
                                >
                                  <span>Review</span>
                                  <I2cIcon
                                      slot="prefix"
                                      name="cs-linear-layers-two"
                                  />
                                </I2cMenuItem>
                                {/* <I2cMenuItem
                            size="small"
                            // onClick={() => navigate('/debit/add')}
                            className="add-project-menu-item"
                          >
                            <span>Download</span>
                            <I2cIcon
                              slot="prefix"
                              name="linear-document-download"
                            />
                          </I2cMenuItem> */}
                                <I2cMenuItem
                                    size="small"
                                    className="add-project-menu-item red"
                                >
                                  <span>Delete</span>
                                  <I2cIcon slot="prefix" name="linear-trash" />
                                </I2cMenuItem>
                              </I2cMenu>
                            </I2cDropdown>
                            <I2cDropdown
                                distance="10"
                                placement="bottom-end"
                                width="calc(var(--i2c-spacing-40) + var(--i2c-spacing-0.5))"
                            >
                              <I2cButton variant="secondary" size="medium">
                                <I2cIcon
                                    name="linear-more"
                                    color="black"
                                    variant="default"
                                />
                                <span className="icons-text">More</span>
                              </I2cButton>
                              <I2cMenu className="add-new-project-menu">
                                <I2cMenuItem
                                    size="small"
                                    onClick={() => navigate("/debit/add")}
                                    className="add-project-menu-item"
                                >
                                  <span>Review</span>
                                  <I2cIcon
                                      slot="prefix"
                                      name="cs-linear-layers-two"
                                  />
                                </I2cMenuItem>
                                {/* <I2cMenuItem
                            size="small"
                            // onClick={() => navigate('/debit/add')}
                            className="add-project-menu-item"
                          >
                            <span>Download</span>
                            <I2cIcon
                              slot="prefix"
                              name="linear-document-download"
                            />
                          </I2cMenuItem> */}
                                <I2cMenuItem
                                    size="small"
                                    className="add-project-menu-item red"
                                >
                                  <span>Delete</span>
                                  <I2cIcon slot="prefix" name="linear-trash" />
                                </I2cMenuItem>
                              </I2cMenu>
                            </I2cDropdown>
                          </div>
                        </div>
                      </div>
                  );
                })}
                <I2cPagination
                    className="users-list-pagination"
                    quickButtonsAsLink
                    totalCount={pagination.total}
                    pageSize={pagination.per_page}
                    currentPage={currentPage}
                    setPage={setCurrentPage}
                />
              </div>
          ) : (
              <div i2c-grid="6@md offset-4@md" className="no-user-container">
                <I2cIcon
                    className="cs-icon-shadowed"
                    variant="shadowed"
                    name="linear-info-circle"
                />
                <div className="no-user-title">No Client(s) Request</div>
                <div className="no-user-text">
                  It seems like there are currently no clients matching your search
                  criteria or available in the system.
                </div>
              </div>
          )}
        </div>
        {(isLoading || isFetching) && <OverlayLoader />}
        
        {/* Toast Notifications */}
        <div>
          <ToastContainer
            successAlertRef={successAlertRef}
            errorAlertRef={errorAlertRef}
            toastMessage={toastMessage}
          />
        </div>
      </React.Fragment>
  );
};

export default UserProjects;
